import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Music2, LayoutDashboard, Building2, Store, Cpu, Users, Calendar, Megaphone, Sparkles, ScrollText, Settings, LogOut, Radio, Mic, Sliders, Clock, Zap, Smartphone, Menu, X } from 'lucide-react';
import toast from 'react-hot-toast';

const NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/tenants', icon: Building2, label: 'Brands' },
  { to: '/stores', icon: Store, label: 'Stores' },
  { to: '/devices', icon: Cpu, label: 'Devices' },
  { to: '/player-preview', icon: Smartphone, label: 'Player Preview' },
  { to: '/users', icon: Users, label: 'Users' },
  { divider: true, label: 'CONTENT' },
  { to: '/schedule', icon: Calendar, label: 'Schedule' },
  { to: '/campaigns', icon: Megaphone, label: 'Campaigns' },
  { to: '/ai-content', icon: Sparkles, label: 'AI Studio' },
  { to: '/dj-studio', icon: Mic, label: 'DJ Studio' },
  { to: '/music-library', icon: Music2, label: 'Music Library' },
  { to: '/audio-editor', icon: Radio, label: 'Audio Editor' },
  { to: '/playlist-builder', icon: Radio, label: 'Playlist Builder' },
  { to: '/rotation-manager', icon: Radio, label: 'Rotation Manager' },
  { to: '/clock-builder', icon: Clock, label: 'Clock Builder' },
  { to: '/rules-engine', icon: Sliders, label: 'Rules Engine' },
  { to: '/automation', icon: Zap, label: 'Automation' },
  { divider: true, label: 'SYSTEM' },
  { to: '/audit', icon: ScrollText, label: 'Audit Trail' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

function NavItems({ onNavigate }) {
  return NAV.map((item, i) => {
    if (item.divider) {
      return (
        <div key={`div-${i}`} className="sidebar-section-label">{item.label}</div>
      );
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.exact}
        data-tooltip={item.label}
        className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
        onClick={onNavigate}
      >
        <item.icon size={16} />
        <span>{item.label}</span>
      </NavLink>
    );
  });
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div className="app-layout">
      <header className="mobile-topbar">
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          {navOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
        <div className="mobile-topbar-title">InStore</div>
      </header>

      {navOpen && (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="Close navigation"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside className={`app-sidebar${navOpen ? ' is-open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-mark">
            <Radio size={16} color="white" />
          </div>
          <div>
            <div className="sidebar-logo-text">InStore</div>
            <div className="sidebar-logo-sub">Media Platform</div>
          </div>
        </div>

        <nav className="app-sidebar-nav" aria-label="Primary">
          <NavItems onNavigate={() => setNavOpen(false)} />
        </nav>

        <div className="sidebar-footer-block">
          <div className="sidebar-user">
            <div className="sidebar-user-name">{user?.full_name}</div>
            <div className="sidebar-user-email">{user?.email}</div>
            <div style={{ marginTop: 6 }}>
              <span className="badge badge-purple">{user?.roles?.[0]?.name || 'Admin'}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }}>
            <LogOut size={14} /> <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="app-main">
        <div className="app-main-inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
